import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, LinearProgress, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { AdminNursesSection, BlockedNursesSection, CancerHistorySection, ProfileSection, RelatedNursesSection } from '../components/patient';
import { getPatient } from '../api/patients';
import { Patient } from '../types/patient';
import { DeveloperInfoSection } from "../components/common";
import { DEVELOPER_THEME } from '../types/common';

/**
 * 患者詳細画面
 */
const PatientDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [ patient, setPatient ] = useState<Patient | null>(null);
  const [ loading, setLoading ] = useState(true);
  const [ error, setError ] = useState<string | null>(null);
  const [ nursesError ] = useState<string | null>(null);
  const fetchPatient = async() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      // Convert id string to number
      const patientId = parseInt(id, 10);
      if (isNaN(patientId)) {
        throw new Error('Invalid patient ID');
      }
      const data = await getPatient(patientId);
      if (data) {
        setPatient(data);
        // データを設定
      } else {
        setError('患者データが見つかりませんでした。');
      }
    } catch (err) {
      console.error('患者データの取得に失敗しました', err);
      setError('患者データの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchPatient();
  }, [ id ]);
  const handlePatientUpdate = () => {
    // 患者情報を再取得
    fetchPatient();
  };
  // 戻るボタンのハンドラ
  const handleBack = () => {
    navigate('/patients');
  };
  if (loading) {
    return (
      <Box sx={{ my: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <LinearProgress sx={{ width: '100%', mb: 2 }}/>
        <Typography variant="body1">患者データを読み込み中...</Typography>
      </Box>
    );
  }
  if (error || !patient) {
    return (
      <Box sx={{ my: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Alert severity="error">{error || '患者データの読み込みに失敗しました。'}</Alert>
        <Button
          startIcon={<Button/>}
          sx={{ mt: 2 }}
          onClick={handleBack}
        >
          一覧に戻る
        </Button>
      </Box>
    );
  }
  return (
    <Box>
      {/* 戻るボタン */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
        <Button
          onClick={handleBack}
          startIcon={<ArrowBackIcon/>}
          variant="outlined"
          color="primary"
        >
          患者一覧に戻る
        </Button>
      </Box>

      {/* テストアカウント警告バナー */}
      {patient.is_test_account && (
        <Alert
          severity="warning"
          sx={{
            mb: 3,
            backgroundColor: DEVELOPER_THEME.BACKGROUND,
            border: `2px solid ${DEVELOPER_THEME.BORDER}`,
            borderRadius: 1,
            '& .MuiAlert-icon': { color: DEVELOPER_THEME.DARK },
            '& .MuiAlert-message': { fontWeight: 'bold', color: DEVELOPER_THEME.DARK }
          }}
        >
          🧪 このアカウントはテスト用データです
        </Alert>
      )}

      {/* 基本情報カード（がん罹患歴を含む） */}
      <ProfileSection
        patient={patient}
        cancerHistoriesContent={<CancerHistorySection cancerHistories={patient.cancer_histories}/>}
        onPatientUpdate={handlePatientUpdate}
      />

      <DeveloperInfoSection
        data={{
          'user_id': patient.user_id,
          'tokens (for auth)': patient.tokens ?? {},
          'fcm_tokens': patient.fcm_tokens ?? {}
        }}
      />

      {/* 対話したことがある看護師一覧 */}
      <Box sx={{ mb: 4 }}>
        <RelatedNursesSection
          patient={patient}
          loadingError={nursesError}
        />
      </Box>

      {/* ブロックした看護師一覧 */}
      <Box sx={{ mb: 4 }}>
        <BlockedNursesSection patient={patient}/>
      </Box>

      {/* 運営相談ルーム（看護師）一覧 */}
      <Box sx={{ mb: 4 }}>
        <AdminNursesSection patient={patient}/>
      </Box>
    </Box>
  );
};
export default PatientDetail;
